import { connect } from 'react-redux';
import { pipe } from 'fp-ts/function';
import { Checkbox, DropDownButton, DropdownItem, IconName, Stack } from '@optimus-web/core';
import { LocalizedText, lt, showSuccessAlert, useFeatureFlag, useToggle } from '@mews/framework';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { compactArray } from '@mews/fp';
import { fetchCurrentEnterpriseReservationsByReservationIds } from '@mews-commander/legacy-entities/src/actions/reservations';
import { checkIn } from '@mews-commander/legacy-entities/src/ducks/reservations';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { useInvalidateReservationGroupQuery } from '@mews-commander/core/entities';
import {
    selectionIsCheckedIn,
    selectionIsCheckinable,
    selectionIsCuttable,
} from '../../selectors/reservationModuleState/reservationSelection';
import { withActiveWindowLoader } from '../../../../core';
import { RESERVATION_ORDER_ITEMS_EXTENT } from '../../constants';
import { CheckInButton } from '../CheckInButton';
import { setResevationResourcesInspected, skipReservationsTodayCleaning } from '../../actions';
import { hasSelectionPreauthorizationEnabled } from '../../selectors';
import { KeyCutterButton } from '../KeyCutterButton/KeyCutterButton';
import { getReservationsAggregatedHousekeepingFlags } from './typedSelectors';
import { HousekeepingOptionFlags } from './utils';

interface OwnProps {
    reservationIds: string[];
    scope: string;
}
interface StateProps {
    isCheckedIn: boolean;
    isCheckinable: boolean;
    isCuttable: boolean;
    isSomeServicePreauthorizationEnabled: boolean;
    flags: HousekeepingOptionFlags;
}
interface DispatchProps {
    handleCheckIn: (disablePreauthorization: boolean) => void;
    setInspected: () => void;
    skipTodayCleaning: () => void;
}
type Props = OwnProps & StateProps & DispatchProps;

const ResourceAssignTableActions = ({
    reservationIds,
    isCheckedIn,
    isCheckinable,
    isCuttable,
    isSomeServicePreauthorizationEnabled,
    flags,
    scope,
    handleCheckIn,
    setInspected,
    skipTodayCleaning,
}: Props) => {
    const isReservationModuleReactQueryEnabled = useFeatureFlag(FeatureFlag.reservationModuleReactQueryEnabled);
    const invalidateGroupQuery = useInvalidateReservationGroupQuery(scope);
    const dispatch = useCommanderThunkDispatch();
    const refetch = async () => {
        if (isReservationModuleReactQueryEnabled) {
            await invalidateGroupQuery();
        } else {
            await dispatch(
                fetchCurrentEnterpriseReservationsByReservationIds({
                    reservationIds,
                    extent: RESERVATION_ORDER_ITEMS_EXTENT,
                })
            );
        }
    };
    const [isPreauthorizationCheckDisabled, toggleIsPreauthorizationCheckDisabled] = useToggle();

    const areMultipleReservationsSelected = reservationIds.length > 1;
    const menuItemProps = compactArray([
        flags.makeSpaceInspected
            ? {
                  label: <LocalizedText textKey={TextKey.MakeSpaceInspected} />,
                  onClick: setInspected,
                  'data-test-make-inspected-bulk': true,
                  key: 'make-inspected',
              }
            : null,
        flags.skipTodayCleaning
            ? {
                  label: <LocalizedText textKey={TextKey.SkipTodayCleaning} />,
                  onClick: skipTodayCleaning,
                  'data-test-skip-next-cleaning-bulk': true,
                  key: 'skip-next-cleaning',
              }
            : null,
    ]);
    return (
        <Stack spacing="100" vertical>
            {areMultipleReservationsSelected && isCheckinable && isSomeServicePreauthorizationEnabled ? (
                <Checkbox checked={isPreauthorizationCheckDisabled} onChange={toggleIsPreauthorizationCheckDisabled}>
                    <LocalizedText textKey={TextKey.PreauthorizationInvalidCheckIn} />
                </Checkbox>
            ) : null}

            <Stack alignment="end" spacing="100">
                {menuItemProps.length !== 0 ? (
                    <DropDownButton
                        label={<LocalizedText textKey={TextKey.Housekeeping} />}
                        data-test-button="housekeeping"
                        variant="ghost"
                    >
                        {menuItemProps.map((props) => (
                            <DropdownItem {...props} />
                        ))}
                    </DropDownButton>
                ) : null}

                {areMultipleReservationsSelected && isCuttable ? (
                    <KeyCutterButton
                        isCheckedIn={isCheckedIn}
                        reservationIds={reservationIds}
                        textKey={TextKey.CreateKeys}
                    />
                ) : null}

                {areMultipleReservationsSelected && isCheckinable ? (
                    <CheckInButton
                        onClick={() => {
                            handleCheckIn(isPreauthorizationCheckDisabled);
                            void refetch();
                        }}
                        variant="primary"
                        icon={IconName.Done}
                        textKey={TextKey.CheckInAll}
                        data-test-check-in-bulk
                        type="submit"
                    />
                ) : null}
            </Stack>
        </Stack>
    );
};

export default pipe(
    ResourceAssignTableActions,
    connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
        (state, { scope, reservationIds }) => ({
            isCheckedIn: selectionIsCheckedIn(state, { scope }),
            isCheckinable: selectionIsCheckinable(state, { scope }),
            isCuttable: selectionIsCuttable(state, { scope }),
            isSomeServicePreauthorizationEnabled: hasSelectionPreauthorizationEnabled(state, { scope }),
            flags: getReservationsAggregatedHousekeepingFlags(state, { reservationIds }),
        }),
        (dispatch: CommanderThunkDispatch, { reservationIds }) => ({
            handleCheckIn: async (disablePreauthorization) =>
                withActiveWindowLoader(async () => {
                    await dispatch(checkIn(reservationIds, disablePreauthorization));

                    showSuccessAlert({ description: lt('ReservationCheckInSuccessMessage') });
                }),
            setInspected: async () =>
                withActiveWindowLoader(async () => {
                    await dispatch(setResevationResourcesInspected({ reservationIds }));
                    showSuccessAlert({ description: lt('MakeSpaceInspectedSuccessMessage') });
                }),

            skipTodayCleaning: async () =>
                withActiveWindowLoader(async () => {
                    await dispatch(skipReservationsTodayCleaning(reservationIds));
                    showSuccessAlert({ description: lt('SaveSuccessMessage') });
                }),
        })
    )
);
